import { faCalculator, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import styled from 'styled-components';
import FancyDropdown from './FancyDropdown';
import TextInput from './input/TextInput';
import Spacer from './Spacer';
import { CostCalculator_service } from './__generated__/CostCalculator_service.graphql';
import Decimal from 'decimal.js';
import { formatCurrency } from '../helpers';

const Container = styled.div`
  padding: 20px;
  background: #f8f9ff;
  border-radius: 10px;
  transition: all 0.4s;
  overflow: hidden;
  max-height: 2000px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const TitleText = styled.div`
  color: #9092c1;
  font-weight: 600;
  font-size: 11px;
`;

const OpenButton = styled.div`
  padding: 6px 8px;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #edeef3;
  }
`;

const LeftHeaderItems = styled.div`
  display: flex;
  align-items: center;
`;

const Content = styled.div``;

const TotalCostContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const CostContainer = styled.div``;

const Cost = styled.div`
  color: #5d6ef8;
  font-size: 25px;
  font-weight: 600;
`;

const CostExtraInfo = styled.div`
  color: #b8b9d8;
  font-size: 11px;
`;

const PerSplitter = styled.div`
  color: #b8b9d8;
  font-size: 22px;
  font-weight: 400;
`;

const FancyTable = styled.div``;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-around;
`;

const TableHeaderItem = styled.div`
  width: 100px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
`;

const TableContent = styled.div``;

const TableRow = styled.div`
  display: flex;
  justify-content: space-around;
  background: white;
  border-radius: 5px;
  padding: 4px;
  align-items: center;
`;

const TableRowItem = styled.div`
  width: 100px;
  font-size: 14px;
  display: flex;
  align-items: center;
  font-weight: 300;
`;

const Units = styled.div`
  color: #9092c1;
  font-weight: 500;
  font-size: 11px;
`;

type Props = {
  service: CostCalculator_service;
  subscriptionFee: number;
  initialDropped?: Boolean;
};

export function CostCalculator({
  service,
  initialDropped,
  subscriptionFee,
}: Props) {
  const [dropped, setDropped] = React.useState(initialDropped || false);

  const items = [
    { value: 100000, label: '100,000' },
    { value: 10000, label: '10,000' },
    { value: 1000, label: '1,000' },
    { value: 100, label: '100' },
    { value: 1, label: '1' },
  ];

  const defaultItem = 2;

  let currency = 'gbp';
  const [charges, setCharges] = React.useState(
    service.charges?.edges
      ?.filter((item) => item !== null && item.node !== null)
      ?.map((item: any) => ({ ...item.node, inpVal: 1 })) ?? []
  );

  const [requests, setRequests] = React.useState(items[defaultItem].value);

  const cost =
    charges.reduce((acc, item) => {
      const cost = new Decimal(item?.price.value ?? 0).add(
        new Decimal(item?.salesTax.value ?? 0)
      );
      let amount = new Decimal(0);
      try {
        amount = new Decimal(item.inpVal);
      } catch (err) {
        amount = new Decimal(0);
      }
      currency = item?.price.currency;
      return cost.mul(amount).add(acc);
    }, new Decimal(0)) ?? 0;

  const total = new Decimal(requests).mul(new Decimal(cost)).toNumber();

  return (
    <Container style={dropped ? {} : { maxHeight: '67px' }}>
      <Header onClick={() => setDropped(!dropped)}>
        <LeftHeaderItems>
          <FontAwesomeIcon icon={faCalculator} color={'#9092C1'} />
          <Spacer horizontal spacing={2} />
          <TitleText>COST CALCULATOR</TitleText>
        </LeftHeaderItems>
        <OpenButton>
          <FontAwesomeIcon icon={faChevronDown} color={'#9092C1'} size='1x' />
        </OpenButton>
      </Header>
      <Spacer vertical spacing={2} />
      <Content>
        <TotalCostContainer>
          <CostContainer>
            <Cost>{formatCurrency(currency, total)}</Cost>
            {cost > 0 && (
              <CostExtraInfo>
                +{formatCurrency(currency, subscriptionFee)}
                /month
              </CostExtraInfo>
            )}
          </CostContainer>
          <Spacer horizontal spacing={1} />
          <PerSplitter>/</PerSplitter>
          <Spacer horizontal spacing={1} />
          <FancyDropdown
            items={items}
            defaultItem={defaultItem}
            onChange={(req) => setRequests(req)}
          />
        </TotalCostContainer>
        <Spacer vertical spacing={3} />
        <TitleText>COST PER REQUEST</TitleText>
        <Spacer vertical spacing={2} />
        <FancyTable>
          <TableHeader>
            <TableHeaderItem>Charge</TableHeaderItem>
            <TableHeaderItem>Cost</TableHeaderItem>
            <TableHeaderItem>Quantity</TableHeaderItem>
          </TableHeader>
          <Spacer vertical spacing={2} />
          <TableContent>
            {charges.map((charge, index) => (
              <>
                <TableRow>
                  <TableRowItem>{charge.name}</TableRowItem>
                  <TableRowItem>
                    {charge.price.value > 0
                      ? `${formatCurrency(
                          currency,
                          new Decimal(charge.price.value)
                            .add(new Decimal(charge.salesTax.value))
                            .toNumber()
                        )}`
                      : 'Free'}
                    {charge.chargeType === 'dynamic' && (
                      <Units>
                        {'\u00A0'}/{charge.metricName}
                      </Units>
                    )}
                  </TableRowItem>
                  <TableRowItem style={{ width: 124 }}>
                    <TextInput
                      type={'number'}
                      value={charge.inpVal}
                      onChange={(val) => {
                        const newC = [...charges];
                        newC[index].inpVal = val;
                        setCharges(newC);
                      }}
                      inputStyles={{ padding: '7px 14px', fontSize: '13px' }}
                    />
                    {charge.chargeType === 'dynamic' && (
                      <Units>{charge.metricName}</Units>
                    )}
                  </TableRowItem>
                </TableRow>
                <Spacer vertical spacing={1} />
              </>
            ))}
          </TableContent>
        </FancyTable>
      </Content>
    </Container>
  );
}

export default createFragmentContainer(CostCalculator, {
  service: graphql`
    fragment CostCalculator_service on Service {
      charges(first: 100) {
        edges {
          node {
            id
            chargeType
            name
            description
            price {
              value
              currency
            }
            metricName
            salesTax {
              value
              currency
            }
          }
        }
      }
    }
  `,
});
